import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import logo from '../../logo.png';
import { MessageSquare, Plus, Trash2, X, Users, Send, CheckCircle, Inbox, Reply, Check } from 'lucide-react';
import Layout from '../../components/Layout/Layout';

function formatearFechaEvento(fecha) {
  if (!fecha) return '';
  const d = new Date(fecha);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('es-MX', { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

function insertarFechaEvento(contenido, fechaProgramada) {
  if (!contenido) return contenido;
  const fechaTexto = formatearFechaEvento(fechaProgramada);
  if (!fechaTexto) return contenido;
  if (/{FECHA_EVENTO}/i.test(contenido)) {
    return contenido.replace(/{FECHA_EVENTO}/gi, fechaTexto);
  }
  return `${contenido}\n\n📅 Fecha: ${fechaTexto}`;
}

export default function Mensajeria() {
  const navigate = useNavigate();
  const [campanas, setCampanas] = useState([]);
  const [catalogos, setCatalogos] = useState({ secciones: [], estructuras: [], roles: [] });
  const [loading, setLoading] = useState(true);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  const [campanaSeleccionada, setCampanaSeleccionada] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [enviandoTelegram, setEnviandoTelegram] = useState(false);

  // ESTADOS DE LA BANDEJA DE ENTRADA
  const [mostrarBandeja, setMostrarBandeja] = useState(false);
  const [mensajesRecibidos, setMensajesRecibidos] = useState([]);
  const [respuestaTexto, setRespuestaTexto] = useState('');
  const [chatActivo, setChatActivo] = useState(null);

  const [formCampana, setFormCampana] = useState({
    nombre_campana: '',
    tipo_canal: '',
    segmento_tipo: '',
    segmento_valor: '',
    contenido: '',
    fecha_programada: ''
  });

  const segmentosConValor = ['por_seccion', 'por_rol', 'por_estructura', 'por_sector', 'por_talento_estatus'];

  useEffect(() => {
    fetchCampanas();
    fetchCatalogos();
  }, []);

  const fetchCampanas = async () => {
    try {
      setLoading(true);
      const res = await api.get('/mensajeria');
      setCampanas(res.data.campanas || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogos = async () => {
    try {
      const res = await api.get('/mensajeria/catalogos');
      setCatalogos(res.data);
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };

  const abrirBandeja = async () => {
    setMostrarBandeja(true);
    try {
      const res = await api.get('/mensajeria/telegram-recibidos');
      setMensajesRecibidos(res.data);
    } catch (error) {
      console.error('Error al cargar bandeja:', error);
    }
  };

  const handleCrearCampana = async (e) => {
    e.preventDefault();
    if (segmentosConValor.includes(formCampana.segmento_tipo) && !formCampana.segmento_valor) {
      setMensaje({ tipo: 'error', texto: 'Debes seleccionar una opción específica para el segmento.' });
      return;
    }

    try {
      const res = await api.post('/mensajeria', formCampana);
      setMensaje({ tipo: 'exito', texto: `Comunicación creada con ${res.data.total_destinatarios} destinatarios.` });
      fetchCampanas();
      setTimeout(() => {
        setMostrarModal(false);
        setMensaje(null);
        setFormCampana({ nombre_campana: '', tipo_canal: '', segmento_tipo: '', segmento_valor: '', contenido: '', fecha_programada: '' });
      }, 2000);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.response?.data?.error || 'Error al crear.' });
    }
  };

  const handleVerDetalle = async (campana) => {
    try {
      const res = await api.get(`/mensajeria/${campana.id}`);
      setCampanaSeleccionada(res.data);
      setMostrarDetalle(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleEnviarTelegram = async (id) => {
    if (!window.confirm('¿Enviar este mensaje masivo por Telegram a todos los vinculados? El envío se hará en segundo plano.')) return;
    try {
      setEnviandoTelegram(true);
      await api.post(`/mensajeria/enviar-telegram/${id}`);
      setMensaje({ tipo: 'exito', texto: '✅ Envío masivo por Telegram iniciado. Los mensajes están saliendo.' });
      setTimeout(() => setMensaje(null), 4000);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.response?.data?.error || 'Error al enviar por Telegram.' });
    } finally {
      setEnviandoTelegram(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta comunicación?')) return;
    try {
      await api.delete(`/mensajeria/${id}`);
      fetchCampanas();
    } catch (error) {
      alert('Error al eliminar.');
    }
  };

  const handleSegmentoTipo = (valor) => {
    setFormCampana(prev => ({ ...prev, segmento_tipo: valor, segmento_valor: '' }));
  };

  const getEstatusColor = (estatus) => {
    switch (estatus) {
      case 'enviado':    return 'bg-verde-100 text-verde-700 border-verde-200';
      case 'borrador':   return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'programado': return 'bg-blue-100 text-blue-700 border-blue-200';
      default:           return 'bg-ink-100 text-ink-700 border-ink-200';
    }
  };

  const getCanalIcono = (canal) => {
    switch (canal) {
      case 'telegram': return '✈️';
      case 'whatsapp': return '📱';
      case 'sms':      return '💬';
      case 'interno':  return '🔔';
      default:         return '📨';
    }
  };

  const getSegmentoEtiqueta = (tipo) => {
    const mapa = {
      todas_personas:  '👥 Todos los promovidos',
      todos_usuarios:  '👤 Todos los usuarios',
      banco_talento:   '⭐ Banco de Talento',
      por_seccion:     '📍 Por sección',
      por_rol:         '🎭 Por rol',
      por_estructura:  '🏛️ Por estructura',
      por_sector:      '🏭 Por sector',
      por_talento_estatus: '⭐ Por estatus de talento',
    };
    return mapa[tipo] || tipo || 'Sin segmento';
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-ink-50">
      <p className="text-ink-500">Cargando mensajería...</p>
    </div>
  );

  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto w-full">

        {/* Header */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-ink-900 flex items-center gap-2">
              <MessageSquare className="text-guinda-700" size={28} />
              Mensajería
            </h2>
            <p className="text-ink-500 text-sm mt-1">Comunicación masiva por sección, estructura y talento</p>
          </div>
        </div>

        {mensaje && (
          <div className={`mb-4 p-4 rounded-xl text-sm font-medium border flex items-center justify-between ${
            mensaje.tipo === 'exito' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <span>{mensaje.texto}</span>
            <button onClick={() => setMensaje(null)}><X size={16} /></button>
          </div>
        )}

        {/* RESUMEN */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl md:rounded-2xl shadow-soft border border-ink-100 p-4 border-l-4 border-guinda-700">
            <p className="text-xs text-ink-500 font-medium uppercase tracking-wide">Total Comunicaciones</p>
            <p className="text-2xl font-bold text-ink-900 mt-1">{campanas.length}</p>
          </div>
          <div className="bg-white rounded-xl md:rounded-2xl shadow-soft border border-ink-100 p-4 border-l-4 border-verde-600">
            <p className="text-xs text-ink-500 font-medium uppercase tracking-wide">Enviadas</p>
            <p className="text-2xl font-bold text-ink-900 mt-1">{campanas.filter(c => c.estatus === 'enviado').length}</p>
          </div>
          <div className="bg-white rounded-xl md:rounded-2xl shadow-soft border border-ink-100 p-4 border-l-4 border-amber-500">
            <p className="text-xs text-ink-500 font-medium uppercase tracking-wide">Borradores</p>
            <p className="text-2xl font-bold text-ink-900 mt-1">{campanas.filter(c => c.estatus === 'borrador').length}</p>
          </div>
          <div className="bg-white rounded-xl md:rounded-2xl shadow-soft border border-ink-100 p-4 border-l-4 border-[#229ED9]">
            <p className="text-xs text-ink-500 font-medium uppercase tracking-wide">Vía Telegram</p>
            <p className="text-2xl font-bold text-ink-900 mt-1">{campanas.filter(c => c.tipo_canal === 'telegram').length}</p>
          </div>
        </div>

        {/* BOTONES */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg text-ink-900 flex items-center gap-2">
            <span className="w-1.5 h-6 rounded-full bg-guinda-700"></span>
            Historial ({campanas.length})
          </h3>
          
          <div className="flex gap-2">
            <button onClick={abrirBandeja}
              className="text-ink-700 bg-white border border-ink-200 font-semibold px-5 py-3 rounded-xl hover:bg-ink-50 transition-all shadow-soft flex items-center gap-2">
              <Inbox size={18} /> Bandeja Telegram
            </button>

            <button onClick={() => setMostrarModal(true)}
              className="text-white font-semibold px-5 py-3 rounded-xl bg-guinda-700 hover:bg-guinda-800 transition-all shadow-soft hover:shadow-md hover:-translate-y-0.5 flex items-center gap-2">
              <Plus size={18} /> Nueva Comunicación
            </button>
          </div>
        </div>

        {/* LISTA DE CAMPAÑAS */}
        {campanas.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-soft border border-ink-100 p-12 text-center">
            <MessageSquare className="text-ink-200 mx-auto mb-3" size={48} />
            <p className="text-ink-500 font-medium">No hay comunicaciones creadas</p>
            <p className="text-ink-400 text-sm mt-1">Crea tu primer mensaje masivo</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {campanas.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl shadow-soft border border-ink-100 p-5 hover:shadow-md transition">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-bold text-ink-900 flex items-center gap-2">
                      {getCanalIcono(c.tipo_canal)} {c.nombre_campana}
                    </h4>
                    <p className="text-xs text-ink-500 mt-1">Por: {c.creador_nombre} {c.creador_apellido}</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border ${getEstatusColor(c.estatus)}`}>
                    {c.estatus}
                  </span>
                </div>

                <p className="text-sm text-ink-700 mb-3 line-clamp-2">{c.contenido}</p>

                <div className="flex items-center gap-2 text-xs text-ink-500 mb-4">
                  <Users size={14} />
                  <span>{c.total_destinatarios} destinatarios</span>
                  <span>•</span>
                  <span>{getSegmentoEtiqueta(c.segmento_tipo)}</span>
                </div>

                <div className="flex gap-2">
                  <button onClick={() => handleVerDetalle(c)}
                    className="flex-1 px-3 py-2 border border-ink-200 rounded-lg text-ink-700 text-sm font-semibold hover:bg-ink-50 transition">
                    Ver detalle
                  </button>
                  <button onClick={() => handleEliminar(c.id)}
                    className="p-2 hover:bg-red-100 rounded-lg transition text-red-500">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-ink-400 text-xs mt-8">DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design</p>
      </div>

      {/* ── MODAL NUEVA COMUNICACIÓN ── */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="bg-guinda-900 px-5 py-4 sticky top-0 z-10 flex items-center justify-between" style={{ borderRadius: '1rem 1rem 0 0' }}>
              <h3 className="text-white font-bold text-base md:text-lg flex items-center gap-2">
                <MessageSquare size={20} className="text-guinda-200" /> Nueva Comunicación
              </h3>
              <button onClick={() => { setMostrarModal(false); setMensaje(null); }} className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded transition">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCrearCampana} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-ink-700 mb-1">Nombre de la Comunicación *</label>
                <input value={formCampana.nombre_campana} onChange={(e) => setFormCampana({ ...formCampana, nombre_campana: e.target.value })}
                  placeholder="Ej: Citatorio Reunión 15 mayo"
                  className="w-full px-3 py-2.5 border border-ink-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700 text-sm" required />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-ink-700 mb-1">Canal</label>
                  <select value={formCampana.tipo_canal} onChange={(e) => setFormCampana({ ...formCampana, tipo_canal: e.target.value })}
                    className="w-full px-3 py-2.5 border border-ink-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700 text-sm">
                    <option value="">Seleccionar...</option>
                    <option value="telegram">✈️ Telegram (Gratis Masivo)</option>
                    <option value="whatsapp">📱 WhatsApp (Nativo)</option>
                    <option value="sms">💬 SMS (Nativo)</option>
                    <option value="interno">🔔 Interno</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-ink-700 mb-1">Segmento</label>
                  <select value={formCampana.segmento_tipo} onChange={(e) => handleSegmentoTipo(e.target.value)}
                    className="w-full px-3 py-2.5 border border-ink-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700 text-sm">
                    <option value="">Seleccionar...</option>
                    <optgroup label="── Generales ──">
                      <option value="todas_personas">👥 Todos los promovidos</option>
                      <option value="todos_usuarios">👤 Todos los usuarios</option>
                      <option value="banco_talento">⭐ Banco de Talento</option>
                    </optgroup>
                    <optgroup label="── Específicos ──">
                      <option value="por_seccion">📍 Por sección electoral</option>
                      <option value="por_rol">🎭 Por rol de usuario</option>
                      <option value="por_estructura">🏛️ Por estructura</option>
                      <option value="por_sector">🏭 Por sector</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              {/* Selectores secundarios */}
              {formCampana.segmento_tipo === 'por_seccion' && (
                <select value={formCampana.segmento_valor} onChange={(e) => setFormCampana({ ...formCampana, segmento_valor: e.target.value })} className="w-full px-3 py-2.5 border border-ink-200 rounded-xl text-sm" required>
                  <option value="">Seleccionar sección...</option>
                  {catalogos.secciones.map(s => (<option key={s.id} value={s.id}>Sección {s.numero_seccion} — {s.municipio}</option>))}
                </select>
              )}
              {formCampana.segmento_tipo === 'por_rol' && (
                <select value={formCampana.segmento_valor} onChange={(e) => setFormCampana({ ...formCampana, segmento_valor: e.target.value })} className="w-full px-3 py-2.5 border border-ink-200 rounded-xl text-sm" required>
                  <option value="">Seleccionar rol...</option>
                  {catalogos.roles.map(r => (<option key={r.valor} value={r.valor}>{r.etiqueta}</option>))}
                </select>
              )}
              {formCampana.segmento_tipo === 'por_estructura' && (
                <select value={formCampana.segmento_valor} onChange={(e) => setFormCampana({ ...formCampana, segmento_valor: e.target.value })} className="w-full px-3 py-2.5 border border-ink-200 rounded-xl text-sm" required>
                  <option value="">Seleccionar estructura...</option>
                  {catalogos.estructuras.map(e => (<option key={e.id} value={e.id}>{e.nombre_estructura}</option>))}
                </select>
              )}

              <div>
                <label className="block text-sm font-semibold text-ink-700 mb-1">Mensaje *</label>
                <textarea value={formCampana.contenido} onChange={(e) => setFormCampana({ ...formCampana, contenido: e.target.value })} rows={5}
                  placeholder="Escribe el mensaje que se enviará..."
                  className="w-full px-3 py-2.5 border border-ink-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700 text-sm resize-none" required />
                <p className="text-xs text-ink-400 mt-1">{formCampana.contenido.length} caracteres</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-ink-700 mb-1">Fecha y Hora del Evento (opcional)</label>
                <input type="datetime-local" value={formCampana.fecha_programada} onChange={(e) => setFormCampana({ ...formCampana, fecha_programada: e.target.value })}
                  className="w-full px-3 py-2.5 border border-ink-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700 text-sm" />
                <p className="text-xs text-ink-500 mt-1">Si la pones, se agregará automáticamente al final del mensaje.</p>
              </div>

              <div className="flex gap-3 mt-2">
                <button type="button" onClick={() => { setMostrarModal(false); setMensaje(null); }}
                  className="flex-1 px-4 py-2.5 border border-ink-300 rounded-xl text-ink-700 font-semibold hover:bg-ink-50 transition text-sm">
                  Cancelar
                </button>
                <button type="submit" className="flex-1 text-white font-bold py-2.5 rounded-xl bg-guinda-700 hover:bg-guinda-800 transition shadow-soft text-sm">
                  Guardar Comunicación
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL DETALLE CAMPAÑA ── */}
      {mostrarDetalle && campanaSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-guinda-900 px-5 py-4 flex items-center justify-between sticky top-0 z-10" style={{ borderRadius: '1rem 1rem 0 0' }}>
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                {getCanalIcono(campanaSeleccionada.campana.tipo_canal)} Detalle de Comunicación
              </h3>
              <button onClick={() => setMostrarDetalle(false)} className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded transition">
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <h4 className="font-bold text-xl text-ink-900 mb-2">{campanaSeleccionada.campana.nombre_campana}</h4>
              <div className="bg-ink-50 rounded-xl p-4 mb-4 border border-ink-100">
                <p className="text-sm text-ink-700 whitespace-pre-wrap">{campanaSeleccionada.campana.contenido}</p>
              </div>

              {/* Botón de envío masivo por Telegram */}
              {campanaSeleccionada.campana.tipo_canal === 'telegram' && (
                <div className="mb-6 p-4 bg-[#229ED9]/10 border border-[#229ED9]/30 rounded-xl flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-sm font-bold text-[#1d84b5]">Envío Masivo por Telegram</p>
                    <p className="text-xs text-ink-500">Se enviará a todos los vinculados en segundo plano.</p>
                  </div>
                  <button onClick={() => handleEnviarTelegram(campanaSeleccionada.campana.id)} disabled={enviandoTelegram}
                    className="flex items-center gap-2 px-4 py-2 bg-[#229ED9] hover:bg-[#1d84b5] text-white rounded-xl text-sm font-semibold transition shadow-soft disabled:opacity-50">
                    <Send size={16} /> {enviandoTelegram ? 'Enviando...' : 'Disparar a Telegram'}
                  </button>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-verde-50 border border-verde-200 rounded-xl p-3 text-center">
                  <p className="text-[10px] text-verde-700 font-bold uppercase tracking-wide">Destinatarios</p>
                  <p className="text-xl font-bold text-verde-800">{campanaSeleccionada.destinatarios.length}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                  <p className="text-[10px] text-blue-700 font-bold uppercase tracking-wide">Enviados</p>
                  <p className="text-xl font-bold text-blue-800">{campanaSeleccionada.destinatarios.filter(d => d.estatus_envio === 'enviado').length}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                  <p className="text-[10px] text-amber-700 font-bold uppercase tracking-wide">Pendientes</p>
                  <p className="text-xl font-bold text-amber-800">{campanaSeleccionada.destinatarios.filter(d => d.estatus_envio === 'pendiente').length}</p>
                </div>
              </div>

              <h5 className="font-bold text-ink-900 mb-2">Lista de destinatarios</h5>
              <div className="max-h-64 overflow-y-auto border border-ink-100 rounded-xl">
                <table className="w-full text-xs">
                  <thead className="bg-ink-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 font-semibold text-ink-600">Nombre</th>
                      <th className="text-left py-2 px-3 font-semibold text-ink-600">Teléfono</th>
                      <th className="text-left py-2 px-3 font-semibold text-ink-600">Estatus</th>
                      <th className="text-left py-2 px-3 font-semibold text-ink-600">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campanaSeleccionada.destinatarios.map((d, i) => (
                      <tr key={i} className="border-b border-ink-50 hover:bg-ink-50/50">
                        <td className="py-2.5 px-3 text-ink-800 font-medium">{d.nombre ? `${d.nombre} ${d.apellido_paterno}` : '—'}</td>
                        <td className="py-2.5 px-3 text-ink-600">{d.telefono}</td>
                        <td className="py-2.5 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${d.estatus_envio === 'enviado' ? 'bg-verde-100 text-verde-700' : 'bg-amber-100 text-amber-700'}`}>
                            {d.estatus_envio}
                          </span>
                        </td>
                        <td className="py-2.5 px-3">
                          {campanaSeleccionada.campana.tipo_canal === 'sms' && (
                            <button onClick={() => {
                                let tel = d.telefono.replace(/\D/g, '');
                                if (tel.length === 10) tel = '52' + tel;
                                const textoFinal = insertarFechaEvento(campanaSeleccionada.campana.contenido, campanaSeleccionada.campana.fecha_programada);
                                window.location.href = `sms:+${tel}${/iPad|iPhone|iPod/.test(navigator.userAgent) ? '&' : '?'}body=${encodeURIComponent(textoFinal)}`;
                              }} className="px-2 py-1 bg-blue-600 text-white rounded text-[10px] font-bold hover:bg-blue-700 transition">
                              💬 SMS
                            </button>
                          )}
                          {campanaSeleccionada.campana.tipo_canal === 'whatsapp' && (
                            <button onClick={() => {
                                let tel = d.telefono.replace(/\D/g, '');
                                if (tel.length === 10) tel = '52' + tel;
                                const textoFinal = insertarFechaEvento(campanaSeleccionada.campana.contenido, campanaSeleccionada.campana.fecha_programada);
                                window.open(`https://wa.me/${tel}?text=${encodeURIComponent(textoFinal)}`, '_blank');
                              }} className="px-2 py-1 bg-verde-500 text-white rounded text-[10px] font-bold hover:bg-verde-600 transition">
                              📱 WhatsApp
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-3 mt-4">
                <button onClick={() => setMostrarDetalle(false)} className="flex-1 px-4 py-2.5 border border-ink-300 rounded-xl text-ink-700 font-semibold hover:bg-ink-50 transition text-sm">
                  Cerrar Detalle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL BANDEJA DE ENTRADA (TELEGRAM) ── */}
      {mostrarBandeja && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="bg-[#229ED9] px-5 py-4 flex items-center justify-between">
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <Inbox size={20} /> Bandeja de Entrada (Telegram)
              </h3>
              <button onClick={() => { setMostrarBandeja(false); setChatActivo(null); }} className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 bg-ink-50">
              {mensajesRecibidos.length === 0 ? (
                <p className="text-center text-ink-400 mt-10">No hay mensajes recibidos.</p>
              ) : (
                <div className="space-y-3">
                  {mensajesRecibidos.map((m) => (
                    <div key={m.id} className={`p-4 rounded-xl border ${m.leido ? 'bg-white border-ink-200' : 'bg-blue-50 border-blue-200 shadow-sm'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-bold text-ink-900 text-sm flex items-center gap-2">
                          {m.usuario_telegram} 
                          {!m.leido && <span className="bg-blue-500 text-white text-[10px] px-2 py-0.5 rounded-full">NUEVO</span>}
                        </p>
                        <span className="text-xs text-ink-400">{new Date(m.fecha).toLocaleString('es-MX')}</span>
                      </div>
                      <p className="text-sm text-ink-700 mb-3">{m.mensaje}</p>
                      
                      <div className="flex gap-2">
                        {!m.leido && (
                          <button onClick={async () => {
                            await api.put(`/mensajeria/telegram-recibidos/${m.id}/leido`);
                            setMensajesRecibidos(prev => prev.map(x => x.id === m.id ? { ...x, leido: true } : x));
                          }} className="text-xs text-blue-600 font-semibold flex items-center gap-1 hover:underline">
                            <Check size={14} /> Marcar como leído
                          </button>
                        )}
                        <button onClick={() => setChatActivo(m)} className="text-xs text-guinda-700 font-semibold flex items-center gap-1 hover:underline ml-auto">
                          <Reply size={14} /> Responder por Telegram
                        </button>
                      </div>

                      {/* Formulario de respuesta inline */}
                      {chatActivo?.id === m.id && (
                        <div className="mt-3 pt-3 border-t border-ink-200">
                          <textarea value={respuestaTexto} onChange={e => setRespuestaTexto(e.target.value)} rows={2}
                            placeholder="Escribe tu respuesta aquí..."
                            className="w-full px-3 py-2 border border-ink-300 rounded-lg text-sm mb-2 focus:ring-2 focus:ring-blue-500 outline-none" />
                          <button onClick={async () => {
                            if (!respuestaTexto) return;
                            await api.post('/mensajeria/telegram-responder', { chat_id: m.chat_id, mensaje: respuestaTexto });
                            setMensajesRecibidos(prev => [{
                              id: Date.now(), chat_id: m.chat_id, usuario_telegram: 'TÚ (Admin)', mensaje: '↩️ ' + respuestaTexto, leido: true, fecha: new Date().toISOString()
                            }, ...prev]);
                            setRespuestaTexto(''); setChatActivo(null);
                          }} className="px-4 py-2 bg-[#229ED9] text-white rounded-lg text-xs font-bold hover:bg-[#1d84b5] transition float-right">
                            Enviar a Telegram
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}